<script setup lang="ts">
import { ref, reactive } from 'vue';
import i18n from '@/i18n';
import { every, omit, cloneDeep, isEmpty } from 'lodash';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { useToast } from 'primevue/usetoast';
import { LANG_ENGLISH, LANG_FRENCH } from '@/constants';
import { PromosEnum } from '@/store/enums/PromosEnum';
import { usePromosStore } from '@/store/PromosStore';
import { useComponentComposable } from '@/components/useComponentComposable';
import { usePromoComposable } from '@/components/admin/usePromoComposable';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputTextAera from 'primevue/textarea';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import TableColumn from 'primevue/column';
import Toast from 'primevue/toast';
import ShowImageByScreen from '@/components/ShowImageByScreen.vue';
import SidePromo from '@/components/promos/SidePromo.vue';
import SidePromoCard from '@/components/promos/SidePromoCard.vue';
import UploadMultipleImages from './UploadMultipleImages.vue';

const { canCreatePromo, canEditPromo, getDataPriview } = usePromoComposable();
const { renderHTMLContent } = useComponentComposable();

const currentLanguage = i18n.global.locale.value;
const promosStore = usePromosStore();
const toast = useToast();
const products = ref();
const listSidePromoInfo = ref();
const isShowError = ref(false);
const visiblePreview = ref(false);
const multipleUploadEn = ref();
const multipleUploadFr = ref();
const headerPreview = ref({
  header: '',
  titleHeader: ''
});

const initPromoPayload = () => {
  return {
    promotionType: PromosEnum.promotionType.SIDE_PROMOS,
    sk: '',
    titleEn: '',
    titleFr: '',
    descEn: '',
    descFr: '',
    termsEn: '',
    termsFr: '',
    imgUrlEn: '',
    imgTitleEn: '',
    imgUrlFr: '',
    imgTitleFr: '',
    order: '',
    imageEn: {
      desktop: '',
      mobile: '',
      tablet: '',
      widescreen: ''
    },
    imageFr: {
      desktop: '',
      mobile: '',
      tablet: '',
      widescreen: ''
    }
  };
};

const payload = ref({ ...initPromoPayload() });
const payloadClone = ref(null);
const sectionTitle = reactive({
  sidePromoTitleEn: '',
  sidePromoTitleFr: ''
});
const selectedProduct = ref([]);
const rules = ref({});
const notRequired = ['sk', 'order'];

for (const property in payload.value) {
  if (notRequired.indexOf(property) === -1) {
    rules.value[property] = {
      required: helpers.withMessage(i18n.global.t('validate.required'), required)
    };
  }
}
const v$ = useVuelidate(rules, payload.value);

const handleRowClick = (e) => {
  payloadClone.value = cloneDeep(listSidePromoInfo.value[e.index]);
  for (const property in payload.value) {
    payload.value[property] = listSidePromoInfo.value[e.index][property];
  }
  handlePreview(payload.value, currentLanguage);
  window.scrollTo(0, 0);
};

const handlePreview = (dataPreview, langguge) => {
  selectedProduct.value = [getDataPriview(dataPreview, langguge)];
};

const handleDelete = () => {
  selectedProduct.value = [];
  promosStore
    .deletePromos(payload.value.sk, PromosEnum.path.SIDE_PROMOS)
    .then(() => {
      clearData();
    })
    .catch((res) => {
      toast.add({ severity: 'error', summary: '', detail: res?.message, life: 5000 });
    });
};

const clearData = () => {
  getSidePromoInfo();
  toast.add({ severity: 'success', summary: '', detail: i18n.global.t('notification.save_successfully'), life: 3000 });
  isShowError.value = false;
  promosStore.isSidePromoUpdate = true;
  payload.value = initPromoPayload();
  multipleUploadEn.value.resetFileUpload();
  multipleUploadFr.value.resetFileUpload();
  selectedProduct.value = [];
};

const handleCancel = () => {
  if (!payloadClone.value) {
    clearData();
    return;
  }
  const oldData = cloneDeep(payloadClone.value) || initPromoPayload();
  for (const property in payload.value) {
    payload.value[property] = oldData[property];
  }
};

const handleSubmit = async () => {
  const isEmptyPayload = every(omit(cloneDeep(payload.value), ['sk', 'promotionType', 'imageEn', 'imageFr']), isEmpty);
  if (isEmptyPayload) {
    toast.add({ severity: 'error', summary: '', detail: i18n.global.t('validate.at_least_field_required'), life: 5000 });
    return;
  }
  isShowError.value = false;

  promosStore
    .submitPromos(payload.value, PromosEnum.path.SIDE_PROMOS)
    .then(() => {
      clearData();
    })
    .finally(() => {
      isShowError.value = false;
    });
};

const onRowReorder = (event) => {
  products.value = event.value;
  handleUpdateOrder(listSidePromoInfo.value, products.value);
};

const handleUpdateOrder = (ListToUpdate, listReference) => {
  Promise.all(
    listReference.map((item1, i) => {
      const itemUpdate = ListToUpdate.find((item2) => item2.sk === item1.sk);
      itemUpdate.order = i;
      return promosStore.submitPromos(itemUpdate, PromosEnum.path.SIDE_PROMOS);
    })
  ).then(() => {
    toast.add({ severity: 'success', summary: i18n.global.t('notification.rows_reordered'), life: 3000 });
    promosStore.isSidePromoUpdate = true;
    getSidePromoInfo();
  });
};

const handleSectionTitleSubmit = async (typeLg) => {
  let payload;
  if (typeLg === LANG_ENGLISH) {
    payload = {
      typeEnum: PromosEnum.sectionPromoType.SIDE_PROMO_TITLE_EN,
      value: sectionTitle.sidePromoTitleEn
    };
  } else {
    payload = {
      typeEnum: PromosEnum.sectionPromoType.SIDE_PROMO_TITLE_FR,
      value: sectionTitle.sidePromoTitleFr
    };
  }

  promosStore
    .submitSectionTitlePromos(payload)
    .then(() => {
      getSectionTitlePromo();
    })
    .finally(() => {
      toast.add({ severity: 'success', summary: '', detail: i18n.global.t('notification.save_successfully'), life: 3000 });
    });
};

const getSidePromoInfo = () => {
  promosStore.getSidePromoInfo().then((res) => {
    listSidePromoInfo.value = res.sort((a, b) => a?.order - b?.order);
    const newList = res.map((item, index) => {
      const product =
        currentLanguage === LANG_ENGLISH
          ? {
              name: item.titleEn,
              description: item.descEn,
              imgTitle: item.imgTitleEn,
              image: item.imgUrlEn,
              renderImage: item.imageEn,
              term: item.termsEn,
              order: index,
              promoTitle: item.titleEn,
              showInB2c: item.showInB2cEn,
              showInB2b: item.showInB2bEn
            }
          : {
              name: item.titleFr,
              description: item.descFr,
              image: item.imgUrlFr,
              renderImage: item.imageFr,
              term: item.termsFr,
              imgTitle: item.imgTitleFr,
              order: index,
              promoTitle: item.titleFr,
              showInB2c: item.showInB2cFr,
              showInB2b: item.showInB2bFr
            };
      return {
        sk: item.sk,
        ...product
      };
    });
    products.value = newList;
  });
};

const getSectionTitlePromo = () => {
  promosStore.getSectionTitlePromo().then((res) => {
    sectionTitle.sidePromoTitleEn = res.sidePromoTitleEn;
    sectionTitle.sidePromoTitleFr = res.sidePromoTitleFr;
  });
};

const openDialogPreview = (language) => {
  handlePreview(payload.value, language);
  visiblePreview.value = true;
  if (language === LANG_ENGLISH) {
    headerPreview.value = {
      header: i18n.global.t('promos.labels.preview_english'),
      titleHeader: sectionTitle.sidePromoTitleEn
    };
    return;
  }
  headerPreview.value = {
    header: i18n.global.t('promos.labels.preview_french'),
    titleHeader: sectionTitle.sidePromoTitleFr
  };
};

const onUploadDataSidePromoEn = (dataImage) => {
  payload.value.imageEn = { ...dataImage };
};

const onUploadDataSidePromoFr = (dataImage) => {
  payload.value.imageFr = { ...dataImage };
};

const handleRemovePreviewImgSidePromo = (key, lang) => {
  if (lang === LANG_ENGLISH) {
    payload.value.imageEn[key] = '';
    return;
  }
  payload.value.imageFr[key] = '';
};

getSidePromoInfo();
getSectionTitlePromo();
</script>

<template>
  <div class="side-promo-admin">
    <Toast position="top-left" group="tl" />
    <Splitter>
      <SplitterPanel>
        <Splitter layout="vertical">
          <SplitterPanel>
            <Splitter>
              <SplitterPanel class="p-5">
                <div class="flex flex-col mb-4">
                  <p class="text-red-600 text-xl font-bold uppercase">{{ $t('promos.section_title_english') }}</p>
                  <InputText :disabled="!canEditPromo" type="text" maxlength="100" :placeholder="$t('promos.section_title_english')" class="mb-1 w-full" v-model.trim="sectionTitle.sidePromoTitleEn" />
                  <Button id="side-promo-title-en-btn" v-if="canEditPromo" :label="$t('promos.save')" icon="pi pi-check" class="p-button-danger w-fit" @click="handleSectionTitleSubmit(LANG_ENGLISH)" />
                </div>
                <h5>{{ $t('promos.labels.english') }}</h5>

                <div class="flex flex-col gap-3">
                  <span class="p-float-label mt-3">
                    <InputText :disabled="!(!payload.sk ? canCreatePromo : canEditPromo)" id="side-promo-imgTitleEn" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.imgTitleEn" />
                    <label for="side-promo-imgTitleEn">{{ $t('promos.labels.image_title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.imgTitleEn.$invalid">
                    {{ v$.imgTitleEn.required.$message }}
                  </span>
                  <div>
                    <UploadMultipleImages
                      ref="multipleUploadEn"
                      @handleUploadData="onUploadDataSidePromoEn($event)"
                      :listUrlPreview="payload.imageEn"
                      @handleRemoveImgReview="(key) => handleRemovePreviewImgSidePromo(key, LANG_ENGLISH)"
                    ></UploadMultipleImages>
                  </div>
                  <span class="p-float-label mt-3">
                    <InputText :disabled="!(!payload.sk ? canCreatePromo : canEditPromo)" id="side-promo-titleEn" type="text" maxlength="100" class="mt-1 mb-1 w-full" v-model.trim="payload.titleEn" />
                    <label for="side-promo-titleEn">{{ $t('promos.labels.title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.titleEn.$invalid">
                    {{ v$.titleEn.required.$message }}
                  </span>
                  <span class="p-float-label mt-3">
                    <InputTextAera :disabled="!(!payload.sk ? canCreatePromo : canEditPromo)" id="side-promo-descEn" class="mb-1 w-full" v-model.trim="payload.descEn" :autoResize="true" rows="5" cols="30" />
                    <label for="side-promo-descEn">{{ $t('promos.labels.description') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.descEn.$invalid">
                    {{ v$.descEn.required.$message }}
                  </span>
                  <span class="p-float-label mt-3">
                    <InputTextAera :disabled="!(!payload.sk ? canCreatePromo : canEditPromo)" id="side-promo-termsEn" class="mb-1 w-full" v-model.trim="payload.termsEn" :autoResize="true" rows="5" cols="30" />
                    <label for="side-promo-termsEn">{{ $t('promos.labels.term') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.termsEn.$invalid">
                    {{ v$.termsEn.required.$message }}
                  </span>
                  <div>
                    <Button id="side-promo-preview-en-btn" :label="$t('promos.labels.preview')" icon="pi pi-eye" class="mt-3 mr-2" @click="openDialogPreview(LANG_ENGLISH)" />
                  </div>
                </div>
              </SplitterPanel>
              <SplitterPanel class="p-5">
                <div class="flex flex-col mb-4">
                  <p class="text-red-600 text-xl font-bold uppercase">{{ $t('promos.section_title_french') }}</p>
                  <InputText :disabled="!canEditPromo" type="text" maxlength="100" :placeholder="$t('promos.section_title_french')" class="mb-1 w-full" v-model.trim="sectionTitle.sidePromoTitleFr" />
                  <Button id="side-promo-title-fr-btn" v-if="canEditPromo" :label="$t('promos.save')" icon="pi pi-check" class="p-button-danger w-fit" @click="handleSectionTitleSubmit(LANG_FRENCH)" />
                </div>
                <h5>{{ $t('promos.labels.french') }}</h5>
                <div class="flex flex-col gap-3">
                  <span class="p-float-label mt-3">
                    <InputText :disabled="!(!payload.sk ? canCreatePromo : canEditPromo)" id="side-promo-imgTitleFr" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.imgTitleFr" />
                    <label for="side-promo-imgTitleFr">{{ $t('promos.labels.image_title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.imgTitleFr.$invalid">
                    {{ v$.imgTitleFr.required.$message }}
                  </span>
                  <div>
                    <UploadMultipleImages
                      ref="multipleUploadFr"
                      @handleUploadData="onUploadDataSidePromoFr($event)"
                      :listUrlPreview="payload.imageFr"
                      @handleRemoveImgReview="(key) => handleRemovePreviewImgSidePromo(key, LANG_FRENCH)"
                    ></UploadMultipleImages>
                  </div>
                  <span class="p-float-label mt-3">
                    <InputText :disabled="!(!payload.sk ? canCreatePromo : canEditPromo)" id="side-promo-titleFr" type="text" maxlength="100" class="mt-1 mb-1 w-full" v-model.trim="payload.titleFr" />
                    <label for="side-promo-titleFr">{{ $t('promos.labels.title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.titleFr.$invalid">
                    {{ v$.titleFr.required.$message }}
                  </span>
                  <span class="p-float-label mt-3">
                    <InputTextAera :disabled="!(!payload.sk ? canCreatePromo : canEditPromo)" id="side-promo-descFr" class="mb-1 w-full" v-model.trim="payload.descFr" :autoResize="true" rows="5" cols="30" />
                    <label for="side-promo-descFr">{{ $t('promos.labels.description') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.descFr.$invalid">
                    {{ v$.descFr.required.$message }}
                  </span>
                  <span class="p-float-label mt-3">
                    <InputTextAera :disabled="!(!payload.sk ? canCreatePromo : canEditPromo)" id="side-promo-termsFr" class="mb-1 w-full" v-model.trim="payload.termsFr" :autoResize="true" rows="5" cols="30" />
                    <label for="side-promo-termsFr">{{ $t('promos.labels.term') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.termsFr.$invalid">
                    {{ v$.termsFr.required.$message }}
                  </span>
                  <div>
                    <Button id="side-promo-preview-fr-btn" :label="$t('promos.labels.preview')" icon="pi pi-eye" class="mt-3 mr-2" @click="openDialogPreview(LANG_FRENCH)" />
                  </div>
                </div>
              </SplitterPanel>
            </Splitter>
          </SplitterPanel>
          <SplitterPanel class="p-3 text-center">
            <Button id="side-promo-save-btn" v-if="!payload.sk ? canCreatePromo : canEditPromo" :label="$t('promos.save')" icon="pi pi-check" class="p-button-secondary mr-2" @click="handleSubmit()" />
            <Button id="side-promo-delete-btn" v-if="payload.sk && canEditPromo" :label="$t('promos.delete')" icon="pi pi-trash" class="mt-3 mr-2" @click="handleDelete()" />
            <Button id="side-promo-cancel-btn" :label="$t('promos.labels.cancel')" icon="pi pi-times" @click="handleCancel()" />
          </SplitterPanel>
        </Splitter>
      </SplitterPanel>
      <SplitterPanel class="side-promo">
        <SidePromo :value="selectedProduct" :section="sectionTitle"></SidePromo>
      </SplitterPanel>
    </Splitter>
    <Dialog :header="headerPreview.header" v-model:visible="visiblePreview" :modal="true">
      <renderHTMLContent class="max-w-400px" :data="headerPreview?.titleHeader" htmlElement="h2"></renderHTMLContent>
      <SidePromoCard :products="selectedProduct[0]" />
    </Dialog>
    <div>
      <Toast />
      <DataTable :value="products" :reorderableColumns="true" @rowReorder="onRowReorder" responsiveLayout="scroll" @row-click="handleRowClick($event)">
        <TableColumn :rowReorder="true" class="w-3rem" :reorderableColumn="false" />
        <TableColumn :header="i18n.global.t('promos.labels.image')">
          <template #body="slotProps">
            <ShowImageByScreen alt="image-render" class="shadow-4 w-64" :images="slotProps.data.renderImage"></ShowImageByScreen>
          </template>
        </TableColumn>
        <TableColumn field="promoTitle" :header="i18n.global.t('promos.labels.title')"></TableColumn>
      </DataTable>
    </div>
  </div>
</template>
